// Shared feedback lifecycle contract (Round 3, R3-H02).
//
// One source of truth for Request-more-info eligibility and the governed note
// length, consumed by the API guard (requestMoreInfoTransactional), the web
// triage control (FeedbackQueuePage), their tests, and Guide 11's documented
// tester journey. Statuses are matched exactly as persisted — no trimming or
// case folding, because a value that differs from the persisted vocabulary is
// itself an "unknown lifecycle state" the caller must refresh to resolve.

export const REQUEST_MORE_INFO_ELIGIBLE_STATUSES = Object.freeze([
  'new',
  'triaged',
  'accepted',
  'in_progress',
]);

// Measured in UTF-16 code units via String.length — identical semantics in the
// current Node and browser runtimes. Exactly 4000 is accepted; 4001 is not.
export const FEEDBACK_COMMENT_MAX_LENGTH = 4000;

const INELIGIBLE_REASONS = Object.freeze({
  converted_to_action: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_CONVERTED',
    message:
      'Request more info is unavailable after this feedback was converted. Use an unconverted item or add a triage note.',
  }),
  converted_to_issue: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_CONVERTED',
    message:
      'Request more info is unavailable after this feedback was converted. Use an unconverted item or add a triage note.',
  }),
  needs_more_info: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_ALREADY_OPEN',
    message:
      "A request for more information is already open. Add a triage note or wait for the reporter's reply.",
  }),
  resolved: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_CLOSED',
    message: 'This feedback request is closed. More information cannot be requested.',
  }),
  rejected: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_CLOSED',
    message: 'This feedback request is closed. More information cannot be requested.',
  }),
  duplicate: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_CLOSED',
    message: 'This feedback request is closed. More information cannot be requested.',
  }),
  deferred: Object.freeze({
    reasonCode: 'REQUEST_MORE_INFO_DEFERRED',
    message:
      'Request more information is unavailable while this feedback is deferred. Use an available workflow action first.',
  }),
});

const STATUS_UNKNOWN = Object.freeze({
  reasonCode: 'REQUEST_MORE_INFO_STATUS_UNKNOWN',
  message: 'The feedback lifecycle state could not be confirmed. Refresh before acting.',
});

export function evaluateRequestMoreInfoEligibility(status) {
  if (typeof status === 'string' && REQUEST_MORE_INFO_ELIGIBLE_STATUSES.includes(status)) {
    return { eligible: true, reasonCode: null, message: null };
  }
  const reason =
    (typeof status === 'string' && INELIGIBLE_REASONS[status]) || STATUS_UNKNOWN;
  return { eligible: false, reasonCode: reason.reasonCode, message: reason.message };
}
